import {PublicTicketsFormDefs} from "../PublicTicketsForm";
import {ShowSelectStage} from "../Stages/ShowSelectStage";
import {DateSelectStage} from "../Stages/DateSelectStage";
import {GlobalState} from "../State/GlobalState";
import {ReservedSeatsSelectStage} from "../Stages/ReservedSeatsSelectStage";
import {TicketTypeSelectStage} from "../Stages/TicketTypeSelectStage";
import {PaymentInfoStage} from "../Stages/PaymentInfoStage";
import {OrderSummaryStage} from "../Stages/OrderSummaryStage";

class StageManager {
    /** @var {GlobalState} */
    globalState;
    /**
     * Here we keep track of all initialized stage instances
     *
     * @var {Object<PublicTicketsFormDefs.Stage,BaseStage>}
     */
    stageInstances;

    initialBrowserHistoryLength

    constructor(globalState) {
        this.globalState = globalState;

        // We store the initial browser history length so we know
        // the point where we cannot pop back
        this.initialBrowserHistoryLength = window.history.length;

        // Init empty stage instances cache
        // noinspection JSValidateTypes
        this.stageInstances = {};

        // Prevent the user from leaving if we are past the first stage
        window.onbeforeunload = () => {
            if (Object.keys(this.stageInstances).length > 1) {
                console.log('Warning the user before he leaves');
                return true;
            }
        }

        // Take care of displaying the proper stage when the user
        // presses back on the browser
        window.onpopstate = (ev) => {
            this.handleBrowserHistoryPopState(ev);
        }
    }

    nextStage() {
        const nextStage = this.getNextVisibleStage(this.globalState.currentStage);

        if (!nextStage) {
            return;
        }

        // Save current stage and re render
        this.globalState.currentStage = nextStage;
        this.renderStage();
    };

    previousStage() {
        const previousStage = this.getPreviousVisibleStage(this.globalState.currentStage);

        if (!previousStage) {
            return;
        }

        // Save current stage and re render
        this.globalState.currentStage = previousStage;
        this.renderStage();
    };

    /**
     * @param {PublicTicketsFormDefs.Stage} stage
     */
    goToStage(stage) {
        if (this.globalState.isStageHidden(stage)) {
            return;
        }

        this.globalState.currentStage = stage;

        this.renderStage();
    };

    canGoToPreviousStage() {
        return this.getPreviousVisibleStage(this.globalState.currentStage) !== null;
    }

    /**
     * @param {PublicTicketsFormDefs.Stage} stage
     * @return {PublicTicketsFormDefs.Stage|null}
     */
    getNextVisibleStage(stage) {
        const currentStageIndex = this.globalState.stageArray.indexOf(stage);

        if (currentStageIndex < 0) {
            return null;
        }

        return this.findVisibleStageByIndex(currentStageIndex + 1, 1);
    }

    /**
     * @param {PublicTicketsFormDefs.Stage} stage
     * @return {PublicTicketsFormDefs.Stage|null}
     */
    getPreviousVisibleStage(stage) {
        let previousStageIndex = this.globalState.stageArray.indexOf(stage) - 1;

        // General admission bypasses reserved seat selection by design.
        if (
            stage === 'seat-type-select' &&
            this.globalState.selectedSeatType === 'general-admission'
        ) {
            previousStageIndex -= 1;
        }

        return this.findVisibleStageByIndex(previousStageIndex, -1);
    }

    /**
     * @param {number} startIndex
     * @param {number} direction
     * @return {PublicTicketsFormDefs.Stage|null}
     */
    findVisibleStageByIndex(startIndex, direction) {
        for (
            let stageIndex = startIndex;
            stageIndex >= 0 && stageIndex < this.globalState.stageArray.length;
            stageIndex += direction
        ) {
            const stage = this.globalState.stageArray[stageIndex];

            if (!this.globalState.isStageHidden(stage)) {
                return stage;
            }
        }

        return null;
    }

    getCurrentStageIndex() {
        return this.globalState.stageArray.indexOf(this.globalState.currentStage);
    }

    getStagesContainer() {
        return document.querySelector('.stages-container');
    }

    /**
     * Renders current stage into the DOM
     */
    renderStage() {
        const state = this.globalState;

        // Keep stage state valid even when the server starts on hidden steps.
        if (state.isStageHidden(state.currentStage)) {
            state.currentStage = this.getNextVisibleStage(state.currentStage)
                || this.getPreviousVisibleStage(state.currentStage);

            if (!state.currentStage) {
                return;
            }
        }

        if (
            state.currentStage === 'seat-select' &&
            state.selectedSeatType === 'general-admission'
        ) {
            state.currentStage = 'seat-type-select';
        }

        let newStageInstance = false;

        // Create instance if not initialized
        if (!this.stageInstances[state.currentStage]) {
            this.stageInstances[state.currentStage] = this.getStageInstance(state.currentStage)
            newStageInstance = true;
        }

        /** @var {BaseStage} */
        const stageInstance = this.stageInstances[state.currentStage];

        // Hide all stages
        this.getStagesContainer().querySelectorAll('.stage').forEach(stage => {
            stage.classList.add('d-none');
        })

        // Display current stage
        stageInstance.display();

        // If we are showing a new stage instance, scroll up
        if (newStageInstance) {
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'instant',
            })
        }

        this.updateBrowserHistory();
    }

    /**
     * Initializes a stage instance based on the current global state
     *
     * @param {PublicTicketsFormDefs.Stage} stage
     */
    getStageInstance(stage) {
        const state = this.globalState;

        switch (stage) {
            case "show-select":
                return this.getShowSelectStage();
            case "date-select":
                // Can't go to date stage without having selected a show first
                if (!state.parentEvent) {
                    setTimeout(() => {
                        // Redirect to show select stage
                        this.goToStage('show-select')
                    });

                    // Warn user and end early, this should never happen anyway
                    alert('Please select a show first');
                    throw 'Invalid stage change request'
                }

                return this.getDateSelectStage();
            case "seat-select":
                if (
                    !state.selectedDate ||
                    !state.selectedSeatType
                ) {
                    setTimeout(() => {
                        // Redirect to date select stage
                        this.goToStage('date-select')
                    });

                    // Warn user and end early, this should never happen anyway
                    alert('Please select a date first');
                    throw 'Invalid stage change request'
                }

                if (state.selectedSeatType === 'reserved') {
                    return this.getReservedSeatSelectStage();
                } else {
                    // TODO: Can we do this without the exception?
                    setTimeout(() => {
                        // Redirect to date select stage
                        this.goToStage('seat-type-select')
                    });

                    throw 'Skipping To Ticket Type Selection (Not an error)'
                }
            case "seat-type-select":
                // Redirect to date select stage
                if (!state.selectedSeatType) {
                    setTimeout(() => {
                        this.goToStage('date-select')
                    });

                    // Warn user and end early, this should never happen anyway
                    alert('Please select a date first');
                    throw 'Invalid stage change request';
                }


                // A mixed Reserved entry may reach this stage without an assignment when GA is available.
                const canContinueWithoutReservedSeats = state.isPublicMixedCompositionEnabled()
                    && state.selectedSeatType === 'reserved'
                    && state.hasGeneralAdmissionCapability();

                // Redirect to seat selects if non set
                if (
                    state.selectedSeatType === 'reserved'
                    && state.selectedSeats.length === 0
                    && canContinueWithoutReservedSeats === false
                ) {
                    setTimeout(() => {
                        this.goToStage('seat-select')
                    });

                    // Warn user and end early, this should never happen anyway
                    alert('Please select seats first');
                    throw 'Invalid stage change request';
                }

                return this.getTicketTypeSelectStage();
            case "payment-info":
                this.validateIfPaymentInfoStageIsReady();

                return this.getPaymentInfoStage();
            case "summary":
                if (!this.globalState.paymentInfo) {
                    setTimeout(() => {
                        // Redirect to date select stage
                        this.goToStage('payment-info')
                    });

                    // Warn user and end early, this should never happen anyway
                    alert('Please add your payment info first');
                    throw 'Invalid stage change request'

                }

                return this.getOrderSummaryStage();
        }
    }

    /**
     * Validates if payment info stage is ready to be displayed
     */
    validateIfPaymentInfoStageIsReady() {
        if (this.globalState.isPublicMixedCompositionEnabled()) {
            if (this.isCombinedTicketSelectionReady() === false) {
                setTimeout(() => {
                    this.goToStage('seat-type-select')
                });

                alert('Please select your tickets first');
                throw 'Invalid stage change request';
            }

            return;
        }

        if (this.globalState.selectedSeatType === 'reserved') {
            let reservedTicketTotalCount = 0;

            this.globalState.reserveTicketCounts.forEach(ticket => {
                reservedTicketTotalCount += ticket.count
            })

            // Only enable next step if total tickets
            // matches number of selected seats
            if (reservedTicketTotalCount !== this.globalState.selectedSeats.length) {
                setTimeout(() => {
                    // Redirect to date select stage
                    this.goToStage('seat-type-select')
                });

                // Warn user and end early, this should never happen anyway
                alert('Please select your tickets first');
                throw 'Invalid stage change request'
            }
        } else {

            if (this.globalState.generalAdmissionTicketCounts.length === 0) {
                setTimeout(() => {
                    // Redirect to date select stage
                    this.goToStage('seat-type-select')
                });

                // Warn user and end early, this should never happen anyway
                alert('Please select your tickets first');
                throw 'Invalid stage change request'
            }
        }
    }

    isCombinedTicketSelectionReady() {
        const state = this.globalState;
        const reservedCountsByGroup = {};
        const selectedSeatsByGroup = {};

        (state.reserveTicketCounts || []).forEach(ticket => {
            if (Number(ticket.count) > 0) {
                reservedCountsByGroup[ticket.groupId] = (reservedCountsByGroup[ticket.groupId] || 0) + Number(ticket.count);
            }
        });

        (state.selectedSeats || []).forEach(seat => {
            selectedSeatsByGroup[seat.seat_group_id] = (selectedSeatsByGroup[seat.seat_group_id] || 0) + 1;
        });

        const reservedCountsMatchAssignments = Object.keys(selectedSeatsByGroup).every(groupId => {
            return reservedCountsByGroup[groupId] === selectedSeatsByGroup[groupId];
        }) && Object.keys(reservedCountsByGroup).every(groupId => {
            return selectedSeatsByGroup[groupId] === reservedCountsByGroup[groupId];
        });

        if (!reservedCountsMatchAssignments) {
            return false;
        }

        if (state.generalAdmissionEnabled !== true && state.hasPositiveGeneralAdmissionTicketCounts()) {
            return false;
        }

        return state.hasPositiveTicketCounts(state.generalAdmissionTicketCounts)
            || state.hasPositiveTicketCounts(state.reserveTicketCounts);
    }

    getShowSelectStage() {
        return new ShowSelectStage(this.globalState);
    }

    getDateSelectStage() {
        return new DateSelectStage(this.globalState);
    }

    getReservedSeatSelectStage() {
        return new ReservedSeatsSelectStage(this.globalState);
    }

    getTicketTypeSelectStage() {
        return new TicketTypeSelectStage(this.globalState);
    }

    getPaymentInfoStage() {
        return new PaymentInfoStage(this.globalState);
    }

    getOrderSummaryStage() {
        return new OrderSummaryStage(this.globalState);
    }

    /**
     * Clear a cached stage instance
     *
     * @param {PublicTicketsFormDefs.Stage} stage
     */
    clearStageInstance(stage) {
        /** @var {BaseStage} */
        const instance = this.stageInstances[stage];

        if (instance) {
            // Clear container
            instance.clearDom();
            delete this.stageInstances[stage];
        }
    }

    updateBrowserHistory() {
        const newStage = this.globalState.currentStage;

        let baseUrl;

        if (this.globalState.baseRoute) {
            // User server defined base routes
            baseUrl = this.globalState.baseRoute + '/s/'; // Add stage url part
        } else {
            // Default fall back
            baseUrl = '/booking/tickets/s/';

            if (this.globalState.isCurrentEventView) {
            baseUrl = '/booking/current-event/s/';
            }
        }

        // Append / at the end of baseUrl if missing
        if (!baseUrl.endsWith('/')) {
            baseUrl += '/';
        }

        // Push current stage key to history
        window.history.pushState(
            {
                stage: newStage,
            },
            '',
            `${baseUrl}${newStage}`
        )
    }

    /**
     * @param {PopStateEvent} event
     */
    handleBrowserHistoryPopState(event) {
        if (!event || !event.state || !event.state.stage) {
            // Not our problem, go back
            window.history.back();
            return;
        }

        /** @var {PublicTicketsFormDefs.Stage} */
        const requestedStage = event.state.stage;

        // Keep popping history if the requested stage is hidden or unknown.
        if (
            !this.globalState.stageArray.includes(requestedStage) ||
            this.globalState.isStageHidden(requestedStage)
        ) {
            window.history.back();
            return;
        }

        const stageExists = this.stageInstances[requestedStage] !== undefined;

        // If the stage does not exists or we are already there
        if (!stageExists || this.globalState.currentStage === requestedStage) {
            // Keep popping history until the user goes to a valid state
            // or out of the page
            window.history.back();
        } else {
            // Go to stage
            this.goToStage(requestedStage)
        }
    }
}

export {StageManager};
